"use client";

import { Button } from "@/components/atoms/buttons";
import { __ } from "@/lib/i18n/functions";
import { Loader2 } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Locales } from "@/lib/i18n";
import { createAccountLocalisedSchema, CreateCompanySchema } from "@/lib/validation/account";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { RefObject, useRef, useState, useTransition } from "react";
import { Input } from "@/components/ui/input";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { cn } from "@/lib/utils";
import { sdk } from "@/lib/medusa/client";
import { useCustomer } from "@/hooks/useCustomer";
import { toast } from "@/components/atoms/toast";
import { Company } from "@/types";
import { getQueryClient } from "@/lib/query";
import { FetchError } from "@medusajs/js-sdk";

type CreateCompanyFormProps = {
  locale: Locales;
  onSuccess: () => void;
  companyToEdit?: Company;
};

const CreateCompanyForm = ({ locale, onSuccess, companyToEdit }: CreateCompanyFormProps) => {
  const { customer, refreshCustomer } = useCustomer();
  const [isPending, startTransition] = useTransition();
  const { createCompanySchema } = createAccountLocalisedSchema(locale, "company");
  const queryClient = getQueryClient();

  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(dropdownRef as RefObject<HTMLDivElement>, () => setShowDropdown(false));

  const form = useForm<CreateCompanySchema>({
    resolver: zodResolver(createCompanySchema),
    defaultValues: {
      companyName: companyToEdit?.name || "",
      email: companyToEdit?.email || "",
      phone: companyToEdit?.phone || "",
      address: companyToEdit?.address || "",
      city: companyToEdit?.city || "",
      vat: companyToEdit?.vat || "",
      zip: companyToEdit?.zip || "",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = form;

  const handleAddressAutocomplete = (value: string) => {
    if (!window.google || !value) {
      setPredictions([]);
      return;
    }

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: value,
        componentRestrictions: { country: ["be"] },
        types: ["geocode"],
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setPredictions(predictions);
          setShowDropdown(predictions.length > 0);
        } else {
          setPredictions([]);
        }
      }
    );
  };

  const handlePredictionSelect = (placeId: string, description: string) => {
    if (!window.google) return;

    const placesService = new window.google.maps.places.PlacesService(document.createElement("div"));

    placesService.getDetails(
      {
        placeId,
        fields: ["address_components", "geometry"],
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          console.error("Place details error", status);
          return;
        }

        const components: Record<string, string> = {};

        place.address_components?.forEach((component) => {
          const types = component.types;
          if (types.includes("postal_code")) {
            components.postal_code = component.long_name;
          }
          if (types.includes("locality")) {
            components.city = component.long_name;
          }
          if (types.includes("route") || types.includes("street_address")) {
            components.address_1 = component.long_name;
          }
        });

        setValue("zip", components.postal_code || "");
        setValue("city", components.city || "");
        setValue("address", description);
        setPredictions([]);
      }
    );
  };

  const onSubmit = (data: CreateCompanySchema) => {
    if (!customer?.id) return;
    startTransition(async () => {
      try {
        if (companyToEdit && companyToEdit.id) {
          await sdk.client.fetch(`store/companies/${companyToEdit.id}`, {
            method: "POST",
            body: {
              name: data.companyName,
              email: data.email,
              phone: data.phone,
              address: data.address,
              city: data.city,
              vat: data.vat,
              zip: data.zip,
            },
          });
          queryClient.invalidateQueries({ queryKey: ["company-details"] });
          toast.success({ title: __("Company Details edited sucessfully !", locale) });
          return;
        }

        await sdk.client.fetch("store/companies", {
          method: "POST",
          body: {
            currency_code: "eur",
            additionalData: {
              customerId: customer?.id,
            },
            name: data.companyName,
            email: data.email,
            phone: data.phone,
            address: data.address,
            city: data.city,
            vat: data.vat,
            zip: data.zip,
            created_by: customer.id,
          },
        });
        await refreshCustomer();
        toast.success({ title: __("Company created successfully!", locale) });
      } catch (error) {
        if (error instanceof FetchError && error.status === 400) {
          console.error("Error from Create Company Form : ", error);
          toast.error({ title: __("A company with this name already exists.", locale) });
          return;
        }
        console.error("Error from Create Company Form : ", error);
        toast.error({ title: __("Something went wrong. Please try again later.", locale) });
      } finally {
        onSuccess();
      }
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
        className="border-primary-light/50 mx-5 space-y-5 border-t pt-5 pb-5.5"
      >
        <FormField
          control={control}
          name="companyName"
          render={({ field }) => (
            <FormItem className="gap-1">
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">{__("Company name", locale)}</FormLabel>
                <FormControl>
                  <Input
                    disabled={isPending}
                    className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-12 placeholder:text-sm focus-visible:ring-[2px]"
                    {...field}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full gap-1">
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">{__("Email", locale)}</FormLabel>
                <FormControl>
                  <Input
                    disabled={isPending}
                    className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                    {...field}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex items-center gap-2.5">
          <FormField
            control={control}
            name="vat"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.phone && !errors.vat && "mb-6")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">{__("VAT Number", locale)}</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isPending}
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="phone"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.vat && !errors.phone && "mb-6")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">{__("Phone", locale)}</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isPending}
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={control}
          name="address"
          render={({ field }) => (
            <FormItem className="gap-1">
              <div className="relative flex flex-col gap-2.5" ref={dropdownRef}>
                <FormLabel className="mb-0 text-base font-medium">{__("Postal Address", locale)}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={isPending}
                    className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                    name={field.name}
                    onBlur={field.onBlur}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      handleAddressAutocomplete(e.target.value);
                    }}
                    onFocus={() => {
                      if (predictions.length > 0) {
                        setShowDropdown(true);
                      }
                    }}
                  />
                </FormControl>
                {showDropdown && predictions.length > 0 && (
                  <div className="border-border-light absolute top-full right-0 left-0 z-50 mt-1 max-h-60 overflow-auto rounded border bg-white shadow-lg">
                    {predictions.map((prediction) => (
                      <div
                        key={prediction.place_id}
                        className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={() => handlePredictionSelect(prediction.place_id, prediction.description)}
                      >
                        {prediction.description}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex items-center gap-2.5">
          <FormField
            control={control}
            name="zip"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.city && !errors.zip && "mb-6")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">{__("ZIP Code", locale)}</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isPending}
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="city"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.zip && !errors.city && "mb-6")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">{__("City", locale)}</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isPending}
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end">
          <Button
            disabled={isPending}
            size="md"
            variant="rounded"
            className="w-full text-base font-semibold sm:w-fit"
            type="submit"
          >
            {isPending && <Loader2 className="mr-2 animate-spin" />}
            {__("Save", locale)}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CreateCompanyForm;
